import { create } from 'zustand';

/**
 * Campaign Config Store - WHAT the tenant calls things + public API keys
 *
 * Stores campaign terminology (nouns) and public API keys after login:
 * - Participant nouns (singular/plural, e.g., Rider/Riders)
 * - Event nouns (singular/plural, e.g., Ride/Rides)
 * - Public map API keys (Mapbox, Google Places)
 *
 * Separation of concerns:
 * - session auth context: WHO you are + WHAT you can do
 * - campaign-config-store: HOW the tenant labels things + API keys
 * - event-admin-store: Which event you're managing
 */

interface CampaignConfigState {
  /** Singular noun for participants (e.g., "Rider", "Walker", "Runner") */
  participantNounSingular: string;

  /** Plural noun for participants (e.g., "Riders", "Walkers", "Runners") */
  participantNounPlural: string;

  /** Singular noun for events (e.g., "Ride", "Walk", "Run") */
  eventNounSingular: string;

  /** Plural noun for events (e.g., "Rides", "Walks", "Runs") */
  eventNounPlural: string;

  /** Public Mapbox access token for map rendering */
  mapboxToken: string | null;

  /** Public Google Places API key for location autocomplete */
  googlePlacesApiKey: string | null;

  /** Whether the store has been populated from the API */
  isLoaded: boolean;

  // Actions
  setCampaignConfig: (config: {
    participantNounSingular?: string | null;
    participantNounPlural?: string | null;
    eventNounSingular?: string | null;
    eventNounPlural?: string | null;
    mapboxToken?: string | null;
    googlePlacesApiKey?: string | null;
  }) => void;
  clearCampaignConfig: () => void;

  // Helpers
  participantNoun: (plural?: boolean) => string;
  eventNoun: (plural?: boolean) => string;
}

const DEFAULTS = {
  participantNounSingular: 'Rider',
  participantNounPlural: 'Riders',
  eventNounSingular: 'Ride',
  eventNounPlural: 'Rides',
  mapboxToken: null,
  googlePlacesApiKey: null,
} as const;

export const useCampaignConfigStore = create<CampaignConfigState>((set, get) => ({
  ...DEFAULTS,
  isLoaded: false,

  setCampaignConfig: (config) =>
    set({
      participantNounSingular: config.participantNounSingular ?? DEFAULTS.participantNounSingular,
      participantNounPlural: config.participantNounPlural ?? DEFAULTS.participantNounPlural,
      eventNounSingular: config.eventNounSingular ?? DEFAULTS.eventNounSingular,
      eventNounPlural: config.eventNounPlural ?? DEFAULTS.eventNounPlural,
      mapboxToken: config.mapboxToken ?? DEFAULTS.mapboxToken,
      googlePlacesApiKey: config.googlePlacesApiKey ?? DEFAULTS.googlePlacesApiKey,
      isLoaded: true,
    }),

  clearCampaignConfig: () =>
    set({
      ...DEFAULTS,
      isLoaded: false,
    }),

  participantNoun: (plural = false) =>
    plural ? get().participantNounPlural : get().participantNounSingular,

  eventNoun: (plural = false) => (plural ? get().eventNounPlural : get().eventNounSingular),
}));
